'use no memo';
import type { FC } from 'react';
import React, { useState, useEffect } from 'react';
import type { IntlShape } from 'react-intl';
import { defineMessages, useIntl } from 'react-intl';
import { guessTimeZone } from '@confluence/timezone-utils/entry-points/guessTimeZone.ts';
import { isValidTimeZone } from '@confluence/timezone-utils/entry-points/isValidTimeZone.ts';
import { getTranslation } from '@confluence/i18n/entry-points/translation.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

export type TimeAgoProps = {
	date: Date | string | number;
	usePrepositions?: boolean;
	dateFormatOptions?: Record<string, string>;
};

const i18n = defineMessages({
	now: {
		id: 'time-ago.time.now',
		defaultMessage: 'now',
		description:
			'Word for time that is currently happening (timestamp for events which happened < 5 seconds ago)',
	},
	lessThanAMinute: {
		id: 'time-ago.time.lessThanAMinute',
		defaultMessage: 'less than a minute ago',
		description: 'Word for time that is less than a minute ago',
	},
	onPreposition: {
		id: 'time-ago.prepositions.on',
		defaultMessage: 'on {datetime}',
		description: 'Date preposition. e.g. on January 1st 1970',
	},
});

const gradation: {
	threshold?: number;
	unit?: Intl.RelativeTimeFormatUnit;
	format?: (options: {
		intl: IntlShape;
		date: Date;
		locale: string;
		usePrepositions?: boolean;
		dateFormatOptions?: Record<string, string>;
		timeZone?: string | null;
		tenantTimeZone?: string | null;
	}) => string;
	factor?: number;
	granularity?: number;
}[] = [
	// Less than 5 seconds -> "now"
	{
		format: ({ intl }) => intl.formatMessage(i18n.now),
	},
	// Between 5-45 seconds -> "less than a minute ago"
	{
		threshold: 5,
		format: ({ intl }) => intl.formatMessage(i18n.lessThanAMinute),
	},
	// Between 45 seconds-2.5 minutes -> "X minutes ago"
	{
		threshold: 45,
		factor: 60,
		unit: 'minute',
	},
	// Between 2.5-45 minutes -> "X minutes ago" (rounded to 5)
	{
		threshold: 2.5 * 60,
		factor: 60,
		granularity: 5,
		unit: 'minute',
	},
	// Between 45 minutes-24 hours -> "X hours ago"
	{
		threshold: 45 * 60,
		factor: 60 * 60,
		unit: 'hour',
	},
	// After 24 hours -> "full date"
	{
		threshold: 24 * 60 * 60,
		format: ({
			intl,
			date,
			locale,
			usePrepositions,
			dateFormatOptions,
			timeZone,
			tenantTimeZone,
		}) => {
			const datetime = date.toLocaleDateString(locale, {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				timeZone: (() => {
					// Prefer timeZone if provided, otherwise use tenantTimeZone, otherwise use browser timezone
					const tz = timeZone || tenantTimeZone || guessTimeZone();
					// Validate the timezone, use UTC as fallback for invalid values
					return isValidTimeZone(tz) ? tz : 'Etc/UTC';
				})(),
				...dateFormatOptions,
			});

			if (usePrepositions) {
				return intl.formatMessage(i18n.onPreposition, { datetime });
			}

			return datetime;
		},
	},
];

const getGradationIndex = (seconds: number) => {
	let i = gradation.length - 1;
	while (i > 0 && seconds < (gradation[i].threshold || seconds)) i--;
	return i;
};

const getSecondsSince = (date: Date) => (Date.now() - date.getTime()) / 1000;

export const formatTimeAgo = (
	intl: IntlShape,
	date: Date | string | number,
	usePrepositions?: boolean,
	dateFormatOptions?: Record<string, string>,
	timeZone?: string | null,
	tenantTimeZone?: string | null,
): string => {
	const locale = getTranslation().locale;

	const dateObject = new Date(date);
	const seconds = getSecondsSince(dateObject);
	const i = getGradationIndex(seconds);

	const customFormat = gradation[i].format;
	if (customFormat) {
		return customFormat({
			intl,
			date: dateObject,
			locale,
			usePrepositions,
			dateFormatOptions,
			timeZone,
			tenantTimeZone,
		});
	}

	// oxlint-disable-next-line compat/compat
	const localeLanguage = new Intl.Locale(locale).language;
	// oxlint-disable-next-line compat/compat
	const formatter = new Intl.RelativeTimeFormat(localeLanguage, {
		style: 'long',
		numeric: 'auto',
	});

	const granularity = gradation[i].granularity || 1;
	const factor = gradation[i].factor || 1;

	const value = -Math.round(seconds / factor / granularity) * granularity;
	const unit = gradation[i].unit || 'second';

	return formatter.format(value, unit);
};

export const StaticTimeAgo: FC<TimeAgoProps> = ({ date, usePrepositions, dateFormatOptions }) => {
	const intl = useIntl();
	const { timeZone, tenantTimeZone } = useSessionData();

	return (
		<span suppressHydrationWarning>
			{formatTimeAgo(intl, date, usePrepositions, dateFormatOptions, timeZone, tenantTimeZone)}
		</span>
	);
};

export const TimeAgo: FC<TimeAgoProps> = ({ date, ...otherProps }) => {
	const [, setRenders] = useState(0);

	const dateObject = new Date(date);
	const seconds = getSecondsSince(dateObject);
	const i = getGradationIndex(seconds);

	useEffect(() => {
		if (i != gradation.length - 1) {
			const timeToNextGradation = (gradation[i + 1].threshold || seconds) - seconds;

			const timeout = setTimeout(() => {
				setRenders((x) => x + 1);
			}, timeToNextGradation * 1000);

			return () => clearTimeout(timeout);
		}
	}, [i, seconds]);

	return <StaticTimeAgo date={date} {...otherProps} />;
};
